import { BadgeWithIcon } from '@orn/shared-web/components/Badge';
import Dropdown from '@orn/shared-web/components/Dropdown';
import Tooltip, { TooltipTrigger } from '@orn/shared-web/components/Tooltip/Tooltip';
import { AlertTriangleIcon, EyeOffIcon, PlusIcon, RefreshIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import { type FinancialAccount } from '../../hooks/queries/useFinancialAccounts';
import { useDeviceBreakpoints } from '../../hooks/useDeviceBreakpoints';
import { accountAsOf, isCreditCardAccount, isInvestmentAccount } from '../../utils/account/accounts';
import { toCurrency } from '../../utils/formatting';
import AccountMetadata from '../AccountMetadata';
import CreditLimitProgressBar from '../CreditLimit/CreditLimitProgressBar';
import Entity from '../entity';
import PlaidFixConnectionButton from '../plaid/PlaidFixConnectionButton';
import { addInvestmentAccountsLabel } from '../plaid/useAddInvestmentAccounts';
import SyncStatus from '../SyncStatus';

const SpinningRefreshIcon: React.FunctionComponent<{ className?: string }> = ({ className }) => {
  return <RefreshIcon className={cx('animate-spin', className)} />;
};

/**
 * Grid columns a list of desktop `AccountRow`s shares via subgrid: name, credit bar, sync status,
 * balance, and the dots menu. The credit-bar column takes the leftover space; the menu column
 * is reserved even when no row has a menu, so balances always end under the section total. Its
 * 2.75rem minimum is the overhung menu's 1.5rem plus the row's 1.25rem padding, which subgrid
 * folds into the edge track.
 */
export const ACCOUNT_ROW_GRID_CLASS_NAME =
  'grid grid-cols-[minmax(0,max-content)_minmax(0,1fr)_auto_auto_minmax(2.75rem,auto)] gap-x-5';

interface IAccountRowProps {
  financialAccount: FinancialAccount;
  onClick?: (accountId: string) => void;
  /** Force compact layout regardless of screen size (useful for narrow containers like sidebars) */
  compact?: boolean;
  /**
   * Omit the trailing block (balance + sync status, "Updating"/"Error" badge) and the
   * click/hover affordance. Use for non-interactive rows like the unsupported-accounts widget,
   * where balance and sync data don't apply.
   */
  hideTrailing?: boolean;
  /** Renders a trailing dots menu with a "Hide account…" action that invokes this callback. */
  onHide?: () => void;
  /** Adds "Add investment accounts from <bank>" to the dots menu, rendering the menu if `onHide` did not. */
  onAddInvestments?: () => void;
  /**
   * Set when a total above the row leaves this account out: the balance reads "Not included"
   * with this sentence as its tooltip, so the row never shows a figure the total did not count.
   */
  notIncludedTooltip?: string;
}

/**
 * Renders a single financial account row with avatar, name, metadata, balance, and sync status.
 * The sync status reads `accountAsOf`, the account's own last sync rather than its connection's;
 * the "has synced" rule also needs a balance, since a sync can stamp an account before one lands. A connection that needs repair swaps the sync
 * status for a "Fix connection" button, except on an investment account, which shows both. A
 * trailing dots menu holds whichever of `onAddInvestments` and `onHide` (a destructive "Hide
 * account…") were provided, and is absent when neither was.
 * `notIncludedTooltip` swaps the balance for "Not included"; an account that has never synced
 * keeps its "Updating" or "Error" badge, which already shows no figure.
 * The desktop layout is a subgrid row, so its parent must apply `ACCOUNT_ROW_GRID_CLASS_NAME`.
 */
const AccountRow = ({
  financialAccount,
  onClick,
  compact = false,
  hideTrailing = false,
  onHide,
  onAddInvestments,
  notIncludedTooltip,
}: IAccountRowProps) => {
  const { isDesktop } = useDeviceBreakpoints();
  const useCompactLayout = compact || !isDesktop;

  const isCreditCard = isCreditCardAccount(financialAccount);
  const hasCreditLimit =
    isCreditCard &&
    financialAccount.currentBalance?.creditLimit &&
    parseFloat(financialAccount.currentBalance.creditLimit) > 0;

  const asOf = accountAsOf(financialAccount);
  // A sync can stamp an account before its first balance lands; until then there is no figure to show.
  const hasSyncedBefore = asOf !== null && financialAccount.currentBalance != null;
  const hasPotentialSyncIssue = financialAccount.bankConnection?.hasPotentialSyncIssue;

  const needsReconnection = financialAccount.bankConnection?.needsReconnection ?? false;
  // An investment value is a snapshot, so it keeps its as-of beside the fix button.
  const showAsOf = !needsReconnection || isInvestmentAccount(financialAccount);
  const fixConnectionButton = needsReconnection && (
    <div onClick={(e) => e.stopPropagation()}>
      <PlaidFixConnectionButton bankConnectionId={financialAccount.bankConnection.id} size="xs" />
    </div>
  );

  const balance = notIncludedTooltip ? (
    <div onClick={(e) => e.stopPropagation()}>
      <Tooltip title={notIncludedTooltip}>
        <TooltipTrigger className="text-tertiary text-md font-medium">Not included</TooltipTrigger>
      </Tooltip>
    </div>
  ) : (
    toCurrency(financialAccount.currentBalance?.balance ?? '0')
  );

  const handleClick = () => {
    if (hasSyncedBefore && onClick && !hideTrailing) {
      onClick(financialAccount.id);
    }
  };

  const isInteractive = hasSyncedBefore && !hideTrailing;
  const useGrid = !useCompactLayout && !hideTrailing;
  const hasMenu = Boolean(onHide || onAddInvestments);

  const statusBadge = hasPotentialSyncIssue ? (
    <BadgeWithIcon size="lg" color="error" iconLeading={AlertTriangleIcon}>
      Error
    </BadgeWithIcon>
  ) : (
    <BadgeWithIcon size="lg" color="warning" iconLeading={SpinningRefreshIcon}>
      Updating
    </BadgeWithIcon>
  );

  return (
    <div
      className={cx(
        'col-span-full px-5 py-4',
        useGrid ? 'grid grid-cols-subgrid items-center' : 'flex items-center justify-between gap-5',
        isInteractive ? 'hover:bg-utility-brand-25 cursor-pointer' : 'cursor-default'
      )}
      onClick={handleClick}
    >
      <div className={cx('flex min-w-0 items-center gap-3 overflow-hidden', !useGrid && 'flex-1')}>
        <Entity.Avatar entity={financialAccount} size="md" />
        <div className="flex min-w-0 flex-col gap-0.5 overflow-hidden">
          <span className="text-md truncate font-medium">{financialAccount.name}</span>
          <div className="text-tertiary truncate text-sm font-normal">
            <AccountMetadata
              subtype={hideTrailing ? financialAccount.subtype : undefined}
              bankName={financialAccount.bank.name}
              mask={financialAccount.mask}
            />
          </div>
        </div>
      </div>

      {useGrid && hasCreditLimit && hasSyncedBefore && (
        <div className="@container col-start-2 flex min-w-0 justify-center">
          <CreditLimitProgressBar
            creditLimit={parseFloat(financialAccount?.currentBalance?.creditLimit ?? '0')}
            balance={parseFloat(financialAccount?.currentBalance?.balance ?? '0')}
            className="@min-[16rem]:flex hidden w-full max-w-[406px] gap-2"
            textClassName="text-xs font-medium"
          />
        </div>
      )}

      {useGrid &&
        (hasSyncedBefore ? (
          <>
            <div className="col-start-3 flex items-center justify-end gap-2">
              {fixConnectionButton}
              {showAsOf && <SyncStatus lastSyncedAt={asOf} />}
            </div>
            <div className="text-md text-primary col-start-4 text-right font-medium">{balance}</div>
          </>
        ) : (
          <div className="col-span-2 col-start-3 justify-self-end">{statusBadge}</div>
        ))}

      {!useGrid && !hideTrailing && (
        <div className="flex shrink-0 items-center justify-end">
          {hasSyncedBefore ? (
            <div className="flex flex-col items-end">
              <span className="text-md text-primary font-medium">{balance}</span>
              {fixConnectionButton}
              {showAsOf && <SyncStatus lastSyncedAt={asOf} size="xs" />}
            </div>
          ) : (
            statusBadge
          )}
        </div>
      )}

      {hasMenu && (
        // In the grid, `-ml-3` overhangs the menu column to match the header's total-to-chevron gap.
        <div className={cx('shrink-0', useGrid && 'col-start-5 -ml-3')} onClick={(e) => e.stopPropagation()}>
          <Dropdown.Root>
            <Dropdown.DotsButton className="p-2" />
            <Dropdown.Popover className="w-min">
              <Dropdown.Menu>
                {onAddInvestments && (
                  <Dropdown.Item icon={PlusIcon} onAction={onAddInvestments}>
                    {addInvestmentAccountsLabel(financialAccount.bank.name)}
                  </Dropdown.Item>
                )}
                {onHide && (
                  <Dropdown.Item icon={EyeOffIcon} destructive onAction={onHide}>
                    Hide account&hellip;
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown.Popover>
          </Dropdown.Root>
        </div>
      )}
    </div>
  );
};

export default AccountRow;
